import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';

export interface Profile {
  id: string;
  full_name: string;
  birth_date: string;
  diabetes_type: string;
  control_place: string;
  role: string;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        return undefined;
      }
      
      localStorage.setItem(`profile_${userId}`, JSON.stringify(data));
      return data as Profile;
    } catch (error) {
      return undefined;
    }
  };

  const refreshProfile = async () => {
    const currentUser = user || (await supabase.auth.getUser()).data.user;
    if (currentUser) {
      const profileData = await fetchProfile(currentUser.id);
      if (profileData !== undefined) {
        setProfile(profileData);
      }
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadProfile = async (userId: string) => {
      // Check local storage first to skip database request entirely
      try {
        const cached = localStorage.getItem(`profile_${userId}`);
        if (cached) {
          const parsedCache = JSON.parse(cached);
          if (parsedCache && parsedCache.id) {
            return parsedCache as Profile;
          }
        }
      } catch (err) {
        console.warn('Error reading cached profile:', err);
      }

      try {
        // Add a timeout to prevent hanging indefinitely (increased to 10s)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
        );

        const dbPromise = supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        const result = await Promise.race([dbPromise, timeoutPromise]) as any;
        const { data, error } = result;

        if (error) {
          if (error.code === 'PGRST116') {
            // It's normal to not have a profile yet for new users
            return null;
          }
          console.warn('Error fetching profile:', error);
          return undefined;
        }
        
        localStorage.setItem(`profile_${userId}`, JSON.stringify(data));
        return data as Profile;
      } catch (error) {
        console.warn('Error or timeout fetching profile:', error);
        return undefined;
      }
    };

    const initSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);

          if (session?.user) {
            const profileData = await loadProfile(session.user.id);
            if (mounted && profileData !== undefined) setProfile(profileData);
          }
        }
      } catch (error) {
        console.error('Error initializing session:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initSession();

    // Safety timeout to ensure we don't get stuck in loading state (increased to 12s)
    const safetyTimeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 12000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_IN') {
        // Set loading true only on explicit act, but allow profile to load without resetting to null on error
        setLoading(true);
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          const profileData = await loadProfile(session.user.id);
          if (mounted && profileData !== undefined) setProfile(profileData);
        }
        if (mounted) setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setSession(session);
        setUser(session?.user ?? null);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    if (user) {
      localStorage.removeItem(`profile_${user.id}`);
    }
    await supabase.auth.signOut();
    setProfile(null);
    setUser(null);
    setSession(null);
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
